import { ErrorState, Loading, PageHead, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, RotateCcw, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';

interface DeadLetter {
  id: string;
  consumer: string;
  sourceTopic: string;
  eventType: string | null;
  errorClass: string;
  errorMessage: string;
  attempts: number;
  status: 'open' | 'replayed' | 'discarded';
  createdAt: string;
}

export function DeadLettersPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['dlq'],
    queryFn: () => api<{ items: DeadLetter[] }>('/v1/admin/dead-letters'),
  });
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'replay' | 'discard' }) =>
      api(`/v1/admin/dead-letters/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dlq'] }),
  });

  return (
    <>
      <PageHead eyebrow={t('nav.admin')} title={t('dlq.title')} subtitle={t('dlq.subtitle')} />
      <section className="card">
        {q.isLoading && <Loading />}
        {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
        {q.data?.items.length === 0 && (
          <div className="empty">
            <CheckCircle2 size={24} color="var(--success)" />
            {t('dlq.empty')}
          </div>
        )}
        {!!q.data?.items.length && (
          <div className="table-wrap">
            <table className="table table-responsive">
              <thead>
                <tr>
                  <th>
                    {t('dlq.col.time')} <span className="faint">({fmt.zoneCaption})</span>
                  </th>
                  <th>{t('dlq.col.event')}</th>
                  <th>{t('dlq.col.consumer')}</th>
                  <th>{t('dlq.col.error')}</th>
                  <th>{t('dlq.col.attempts')}</th>
                  <th>{t('dlq.col.status')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((d) => (
                  <tr key={d.id}>
                    <td style={{ whiteSpace: 'nowrap' }} data-label={t('dlq.col.time')}>
                      {fmt.dateTime(d.createdAt)}
                    </td>
                    <td className="mono" style={{ fontSize: 'var(--t-xs)' }} data-label={t('dlq.col.event')}>
                      {d.eventType ?? '—'}
                    </td>
                    <td
                      className="mono faint"
                      style={{ fontSize: 'var(--t-xs)' }}
                      data-label={t('dlq.col.consumer')}
                    >
                      {d.consumer}
                    </td>
                    <td style={{ maxWidth: 320 }} data-label={t('dlq.col.error')}>
                      <span className="badge badge-danger">{d.errorClass}</span>{' '}
                      <span className="mute" style={{ fontSize: 'var(--t-xs)' }}>
                        {d.errorMessage}
                      </span>
                    </td>
                    <td className="num" data-label={t('dlq.col.attempts')}>
                      {fmt.number(d.attempts)}
                    </td>
                    <td data-label={t('dlq.col.status')}>
                      <span className={`badge ${d.status === 'open' ? 'badge-warning' : 'badge-neutral'}`}>
                        {t(`dlq.status.${d.status}`)}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }} data-label="">
                      {d.status === 'open' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn btn-sm"
                            onClick={() => act.mutate({ id: d.id, action: 'replay' })}
                            disabled={act.isPending}
                          >
                            <RotateCcw size={14} />
                            {t('dlq.replay')}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => act.mutate({ id: d.id, action: 'discard' })}
                            disabled={act.isPending}
                          >
                            <Trash2 size={14} />
                            {t('dlq.discard')}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
